/**
 * Download the Job Description (JD) for a given job as a .txt file.
 *
 * Fetches the JD content from the authenticated backend endpoint
 * (`GET /api/jobs/{jobId}/job-description`) and triggers a browser download.
 *
 * The JD belongs to the job (not the candidate), so this utility is keyed by
 * `jobId`. An optional `jobTitle` is used to produce a human-friendly filename;
 * if omitted, we fall back to the server-provided filename.
 *
 * Usage:
 *     import { downloadJobDescription } from '../utils/downloadJobDescription';
 *     await downloadJobDescription({ jobId, jobTitle });
 */

import { apiFetch } from '../services/apiClient';

const sanitizeForFilename = (value) =>
    String(value || '')
        .trim()
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '');

export const downloadJobDescription = async ({ jobId, jobTitle } = {}) => {
    if (!jobId) {
        alert('Cannot download JD: missing job id.');
        return;
    }

    try {
        const response = await apiFetch(`/api/jobs/${jobId}/job-description`);

        if (!response.ok) {
            const errorData = await response
                .json()
                .catch(() => ({ error: `HTTP ${response.status}` }));

            if (response.status === 404) {
                alert('No job description is available for this job.');
                return;
            }

            throw new Error(
                errorData.detail ||
                    errorData.error ||
                    `Failed to fetch job description (${response.status})`
            );
        }

        const data = await response.json();
        const content = data?.content || '';

        if (!content.trim()) {
            alert('Job description is empty for this job.');
            return;
        }

        const sanitizedTitle = sanitizeForFilename(jobTitle);
        const fallbackFilename =
            typeof data?.filename === 'string' && data.filename.trim()
                ? data.filename
                : `job_description_${jobId}.txt`;
        const filename = sanitizedTitle
            ? `JD_${sanitizedTitle}_${jobId}.txt`
            : fallbackFilename;

        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading job description:', error);
        alert(
            `Failed to download job description: ${error.message}. Please try again.`
        );
    }
};

export default downloadJobDescription;
